import React from 'react';
import {
  Box,
  Container,
  Heading,
  Text,
  Image,
  SimpleGrid,
  Flex,
  useColorModeValue,
  Stack,
  VStack,
} from '@chakra-ui/react';

// Keep your existing imports
import supervisor from './img/whygrptch2.svg'; // Grappler Chowdown
import karma from './img/whygrptch4.svg';      // Client Projects
import calculator from './img/whygrptch3.svg'; // Dedicated Mentor
import teamBuilder from './img/whygrptch1.svg'; // Virtual Mode

const Features = () => {
  const cardBg = useColorModeValue("white", "gray.900");
  const headingColor = useColorModeValue("gray.800", "white");
  const textMuted = useColorModeValue("gray.600", "gray.400");
  const borderColor = useColorModeValue("gray.100", "whiteAlpha.100");

  const FeatureCard = ({ title, hook, desc, img, borderCol }) => (
    <Box
      bg={cardBg}
      p={10}
      borderRadius="2xl"
      // One-sided thick accent border
      borderLeft="8px solid"
      borderLeftColor={borderCol}
      // Subtle borders for the rest of the card
      borderRight="1px solid"
      borderBottom="1px solid"
      borderTop="1px solid"
      borderColor={borderColor}
      boxShadow="0 15px 35px -5px rgba(0, 0, 0, 0.05)"
      transition="all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275)"
      _hover={{ 
        transform: "translateX(10px)", // Slides right slightly on hover
        boxShadow: "2xl",
        bg: useColorModeValue("gray.50", "gray.800")
      }}
      maxW="400px"
      w="full"
      role="group"
    >
      <VStack spacing={5} align="flex-start">
        <Heading 
          size="md" 
          fontWeight="900" 
          color={headingColor} 
          fontFamily="Bricolage Grotesque"
          letterSpacing="tight"
          textTransform="uppercase"
        >
          {title}
        </Heading>
        
        <Box>
          <Text 
            fontSize="sm" 
            color={borderCol} // Hook matches the border color
            fontWeight="900" 
            mb={1}
            letterSpacing="wider"
            textTransform="uppercase"
          >
            {hook}
          </Text>
          <Text 
            fontSize="md" 
            color={textMuted} 
            lineHeight="1.8" 
            fontFamily="Poppins"
          >
            {desc}
          </Text>
        </Box>

        <Flex justify="center" w="full" pt={2}>
          <Image 
            src={img} 
            alt={title} 
            boxSize={{ base: "80px", md: "115px" }} 
            objectFit="contain"
            filter="drop-shadow(0 10px 10px rgba(0,0,0,0.05))"
            _groupHover={{ transform: "scale(1.1) rotate(-5deg)" }}
            transition="0.4s ease"
          />
        </Flex>
      </VStack>
    </Box>
  );

  return (
    <Box py={24}>
      <Container maxW="7xl">
        <Stack spacing={4} textAlign="center" mb={20}>
          <Text fontWeight="black" color="#FFA900" letterSpacing="0.2em" textTransform="uppercase" fontSize="xs">
            The Advantage
          </Text>
          <Heading fontFamily="Bricolage Grotesque" size="2xl" color={headingColor} fontWeight="900" letterSpacing="tighter">
            Why GrapplTech Hits Different.
          </Heading>
        </Stack>

        <SimpleGrid columns={{ base: 1, md: 3 }} spacing={12} alignItems="center">
          
          {/* Column 1: Left */}
          <Flex justify="center">
            <FeatureCard 
              title="Grappler Chowdown"
              hook="Eat. Network. Repeat."
              borderCol="#CA3A31"
              img={supervisor}
              desc="We don't just celebrate wins; we fuel them. Exclusive high-tier lunches for our top performers to talk shop and build real bonds."
            />
          </Flex>

          {/* Column 2: Middle */}
          <Stack spacing={12} align="center">
            <FeatureCard 
              title="Virtual Mode"
              hook="Work from Anywhere."
              borderCol="#FFA900"
              img={teamBuilder}
              desc="Forget the commute. Access global internship pipelines and world-class tech companies from your own setup. Your location isn't a limit anymore."
            />
            <FeatureCard 
              title="Dedicated Mentor"
              hook="1:1 Career Coaching."
              borderCol="#028EC4"
              img={calculator}
              desc="Stop guessing your roadmap. Get direct access to industry veterans who help you navigate the noise and land the high-paying roles."
            />
          </Stack>

          {/* Column 3: Right */}
          <Flex justify="center">
            <FeatureCard 
              title="Client Projects"
              hook="Real Stakes. Real Code."
              borderCol="#82B536"
              img={karma}
              desc="Building 'to-do' apps is for amateurs. Here, you ship actual features for real companies, gaining the high-signal experience recruiters crave."
            />
          </Flex>

        </SimpleGrid>
      </Container>
    </Box>
  );
};

export default Features;